import {
  atom,
  useRecoilValue,
  useRecoilTransaction_UNSTABLE,
  GetRecoilValue,
} from "recoil";

import { SimpleSetValue } from "../../../types";
import { QueryStatus } from "../../../services/query";
import activities from "../../../redux/activities";

/////////
// START: Block to be removed once deprecated current operation hook is removed.
/////////

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const farmsGlobalActivityAtom = atom<activities>({
  key: "[@fabric/core] farmsGlobalActivityAtom",
  default: activities.idling,
});
const farmsGlobalStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] farmsGlobalStatusAtom",
  default: "idle",
});
const farmsGlobalFetchStatusAtom = atom<QueryStatus>({
  key: "[@fabric/core] farmsGlobalFetchStatusAtom",
  default: "idle",
});
const farmsGlobalLastUpdatedAtAtom = atom<number>({
  key: "[@fabric/core] farmsGlobalLastUpdatedAtAtom",
  default: Date.now(),
});

const theFetchActivity = activities.setCurrentOperation;

// Returns the global status "module" for farms.
// This is only meant for use in `currentOperation.globalStatus.ts`.
export const getFarmsGlobalStatusModule = (
  get: GetRecoilValue,
): {
  activity: activities;
  status: QueryStatus;
  fetchStatus: QueryStatus;
  lastUpdatedAt: number;
} => ({
  activity: get(farmsGlobalActivityAtom),
  status: get(farmsGlobalStatusAtom),
  fetchStatus: get(farmsGlobalFetchStatusAtom),
  lastUpdatedAt: get(farmsGlobalLastUpdatedAtAtom),
});

// ############################
// ## useFarmsGlobalActivity ##
// ############################

export type ResultOfUseFarmsGlobalActivity = activities;
export type UseFarmsGlobalActivity = () => ResultOfUseFarmsGlobalActivity;

/**
 * @returns The last farms activity, whether from `useFarms` or a mutation hook like `useUpdateFarm`
 *
 * @deprecated Use `useFarms` or a mutation hook like `useUpdateFarm` instead. This is only necessary to support deprecated hooks.
 */
export const useFarmsGlobalActivity: UseFarmsGlobalActivity = () => {
  return useRecoilValue(farmsGlobalActivityAtom);
};

// ##########################
// ## useFarmsGlobalStatus ##
// ##########################

export type ResultOfUseFarmsGlobalStatus = QueryStatus;
export type UseFarmsGlobalStatus = () => ResultOfUseFarmsGlobalStatus;

/**
 * @returns the last status of the last farms activity, whether from `useFarms` or a mutation hook like `useUpdateFarm`.
 *
 * @deprecated Use `useFarms` or a mutation hook like `useUpdateFarm` instead. This is only necessary to support deprecated hooks.
 */
export const useFarmsGlobalStatus: UseFarmsGlobalStatus = () => {
  return useRecoilValue(farmsGlobalStatusAtom);
};

// ########################################
// ## useSetFarmsGlobalActivityAndStatus ##
// ########################################

export type ResultOfUseSetFarmsGlobalActivityAndStatus = SimpleSetValue<{
  activity: activities;
  status: QueryStatus;
}>;
export type UseSetFarmsGlobalActivityAndStatus =
  () => ResultOfUseSetFarmsGlobalActivityAndStatus;

/**
 * Allows setting the last farms activity and
 * the status of that activity.
 *
 * @deprecated This is only necessary to support deprecated hooks.
 */
export const useSetFarmsGlobalActivityAndStatus: UseSetFarmsGlobalActivityAndStatus =
  () => {
    // Using useRecoilTransaction_UNSTABLE ensures that the atoms are updated atomically
    return useRecoilTransaction_UNSTABLE(
      ({ set }) =>
        (payload) => {
          set(farmsGlobalActivityAtom, payload.activity);
          set(farmsGlobalStatusAtom, payload.status);
          set(farmsGlobalLastUpdatedAtAtom, Date.now());

          if (payload.activity === theFetchActivity) {
            set(farmsGlobalFetchStatusAtom, payload.status);
          }
        },
      [],
    );
  };

///////
// END: Block to be removed once deprecated current operation hook is removed.
///////
